import { Chip, Tooltip } from "@mui/material";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  selectViewedCanvasScale,
  selectViewedRectangles,
} from "@store/selectors";
import { scaleCanvas } from "@store/slices/workspaceSlice";
import { setStagePosition } from "@features/annogram/canvas/state/canvasAuxSlice";
import {
  fitTransform,
  offscreenIndices,
  visibleCanvasViewport,
} from "@features/annogram/canvas/utils/fitView";

/**
 * Flags nodes that sit outside the visible viewport (the canvas is
 * deliberately allowed to be larger than the screen). Clicking the
 * chip zooms/pans so everything is visible. Renders nothing when the
 * whole canvas is on screen.
 */
const OffscreenIndicator = () => {
  const dispatch = useAppDispatch();
  const rectangles = useAppSelector(selectViewedRectangles);
  const scale = useAppSelector(selectViewedCanvasScale);
  const stagePosition = useAppSelector(
    (state) => state.canvasAux.stagePosition
  );

  const viewport = visibleCanvasViewport();
  const hidden = offscreenIndices(
    rectangles,
    scale,
    stagePosition,
    viewport
  ).length;

  if (hidden === 0) return null;

  const handleFit = () => {
    // Sample the viewport at click time, not render time: a render can
    // happen while a modal backdrop covers the canvas (sampling then
    // degrades to the full container), and nothing re-renders the chip
    // between the modal closing and the click.
    const fit = fitTransform(rectangles, visibleCanvasViewport());
    if (!fit) return;
    dispatch(scaleCanvas({ scale: fit.scale }));
    dispatch(setStagePosition(fit.position));
  };

  return (
    <Tooltip title="Zoom to fit all nodes">
      <Chip
        icon={<CenterFocusStrongIcon />}
        label={`${hidden} node${hidden === 1 ? "" : "s"} off-screen`}
        color="warning"
        variant="outlined"
        size="small"
        onClick={handleFit}
        sx={{
          position: "fixed",
          bottom: 76,
          right: 16,
          zIndex: 10,
          backgroundColor: "background.paper",
        }}
        aria-label={`${hidden} nodes are off-screen; zoom to fit`}
      />
    </Tooltip>
  );
};

export default OffscreenIndicator;
